/**
 * useEconomicModelingScenarios -- the FI's own Economic Modeling scenarios.
 *
 * Reads GET /api/v1/modeling/scenarios on strategy-tools-api. That surface is
 * tenant-scoped server-side as of GH #2727: the route resolves the caller's
 * tenant through the canonical `resolveEffectiveTenantId` and returns only rows
 * that tenant owns. An Aaim operator on strategy.aaim.com reads across tenants;
 * an FI principal here does not. The client sends no tenant hint of its own --
 * a caller-supplied tenant id is exactly what `multi-tenant-request-shape.md`
 * forbids.
 *
 * The whole surface is gated on Institution.enableOverdraftPreview
 * (`requireModelingEntitlement`, 403-on-OFF), so a 403 means "surface dark for
 * this tenant", not a defect. It is surfaced as such rather than as an error.
 */

import { useQuery } from '@tanstack/react-query';
import type { components as econComponents } from '@aaimplatform/institutional-types/economic-modeling';
import { economicModelingClient } from '../api/client';

/** The contract's own row shape -- see ScenarioSummary in economic-modeling.openapi.yaml. */
export type ModelingScenarioSummary = econComponents['schemas']['ScenarioSummary'];

export type ModelingScenariosState = {
  scenarios: ModelingScenarioSummary[];
  /** True when the tenant does not carry the entitlement (403). */
  notEntitled: boolean;
};

export function useEconomicModelingScenarios() {
  return useQuery<ModelingScenariosState>({
    queryKey: ['economic-modeling', 'scenarios'],
    queryFn: async () => {
      const { data, error, response } = await economicModelingClient.GET(
        '/api/v1/modeling/scenarios',
        {},
      );
      if (response.status === 403) {
        return { scenarios: [], notEntitled: true };
      }
      // `!response.ok` FIRST, and not `if (error)`. openapi-fetch leaves `error`
      // undefined when a non-2xx carries an empty or non-JSON body, so keying on
      // `error` alone let a 500 fall through to the empty-array return and render
      // as "No scenarios yet" -- a failure indistinguishable from a real empty
      // list. The status rides along on the thrown error because the app-wide
      // retry predicate reads it.
      if (!response.ok) {
        const err = new Error(
          (error as { message?: string } | undefined)?.message ??
            `Failed to load scenarios (HTTP ${response.status})`,
        ) as Error & { status?: number };
        err.status = response.status;
        throw err;
      }
      const scenarios = (data as { scenarios?: ModelingScenarioSummary[] } | undefined)
        ?.scenarios;
      // Absent means absent. No synthesized rows, no placeholder scenario.
      return { scenarios: scenarios ?? [], notEntitled: false };
    },
  });
}
